import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal, Select, Spin, Switch, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useQueryClient } from '@tanstack/react-query';

import {
  useCommandCentreCameras,
  useCommandCentreCameraStream,
  useCommandCentreEvents,
  useCommandCentreOperator,
  useCommandCentreOperators,
  useCommandCentrePositions,
  useCommandCentreSites,
  useCommandCentreTrackingThresholds,
} from '@/api/CommandCentre';
import { QUERY_KEYS } from '@/api/queryKeys';
import {
  CustomColumns,
  CustomTable,
  faBriefcase,
  faChevronLeft,
  faChevronRight,
  faMapMarkerAlt,
  faWifi,
  Icon,
  PageContainer,
  Title,
} from '@/ui/components';
import { Map as SiteMap, OperatorMapMarker } from '@/ui/components/Map/Map';
import {
  AppFeatures,
  CommandCentreOperator,
  LiveUpdateRow,
  CommandCentrePosition,
  OperatorPresence,
} from '@/types';
import { useAppCustomers } from '@/hooks/useAppCustomers';

import { useOperatorDecayTimer } from './hooks/useOperatorDecayTimer';
import { useCommandCentreSocket } from './hooks/useCommandCentreSocket';
import { mergeOperatorsWithSitePositions } from './utils/mergeOperatorsWithSitePositions';
import { getWorkrateLevel, WORKRATE_COLORS } from './utils/workrate';
import { presenceCssClass, presenceLabel } from './utils/operatorTracking';
import './CommandCentre.scss';
import { useTranslate } from '@/translations';
import { useTheme } from 'styled-components';
import { useSignalRStatus } from '@/hooks/useWebSockets';
import { useComms } from '@/modules/comms/context/CommsContext';
import { useCall } from '@/modules/comms/call/CallContext';
import { useConversationDrawer } from '@/modules/comms/context/ConversationDrawerContext';
import { CommandCentreCommsBootstrap } from './CommandCentreCommsBootstrap';
import { OperatorConversationButton } from './OperatorConversationButton';
import { OperatorProfileActions } from './OperatorProfileActions';
import { JobAssignmentModal } from './JobAssignmentModal';
import type H from '@here/maps-api-for-javascript';
import { CompoundPreview } from './compound/CompoundPreview';

const EMPTY_POSITIONS: CommandCentrePosition[] = [];

const PANEL_KEYS = {
  liveUpdates: 'commandCentre.hideLiveUpdates',
  cameras: 'commandCentre.hideCameras',
  table: 'commandCentre.hideTable',
  tableExpanded: 'commandCentre.userTableExpanded',
  liveExpanded: 'commandCentre.liveUpdatesExpanded',
  siteId: 'commandCentre.siteId',
} as const;

export const CommandCentrePage: FC = () => {
  const { t } = useTranslate();
  const { accent } = useTheme();
  const queryClient = useQueryClient();
  const { hasConfiguredFeatureAccess } = useAppCustomers();
  const canAllocateJobs = hasConfiguredFeatureAccess(
    AppFeatures.CommandCentreJobAllocation,
  );
  const signalRStatus = useSignalRStatus();
  const { status: commsStatus, directRooms, unreadCounts } = useComms();
  const { status: callStatus, startCall } = useCall();
  const { openConversation: openGlobalConversation } = useConversationDrawer();
  const eventTypeOptions = useMemo(
    () => [
      { label: t('ccEventTypeParking'), value: 'Parking' },
      { label: t('ccEventTypeArrival'), value: 'Arrival' },
      { label: t('ccEventTypeDispatch'), value: 'Dispatch' },
    ],
    [t],
  );
  const { data: sites = [] } = useCommandCentreSites();
  const [siteId, setSiteId] = useState<number | undefined>(() => {
    const stored = sessionStorage.getItem(PANEL_KEYS.siteId);
    return stored ? Number(stored) : undefined;
  });
  const [hideLiveUpdates, setHideLiveUpdates] = useState(
    () => localStorage.getItem(PANEL_KEYS.liveUpdates) === 'true',
  );
  const [hideCameras, setHideCameras] = useState(
    () => localStorage.getItem(PANEL_KEYS.cameras) === 'true',
  );
  const [hideTable, setHideTable] = useState(
    () => localStorage.getItem(PANEL_KEYS.table) === 'true',
  );
  const [tableExpanded, setTableExpanded] = useState(
    () => sessionStorage.getItem(PANEL_KEYS.tableExpanded) === 'true',
  );
  const [liveExpanded, setLiveExpanded] = useState(
    () => sessionStorage.getItem(PANEL_KEYS.liveExpanded) === 'true',
  );
  const [satelliteView, setSatelliteView] = useState(false);
  const [compoundMap, setCompoundMap] = useState<H.Map | null>(null);
  const [compoundPreviewActive, setCompoundPreviewActive] = useState(false);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>();
  const [selectedUserId, setSelectedUserId] = useState<number>();
  const [jobAssignmentOperator, setJobAssignmentOperator] = useState<{
    userId: number;
    displayName: string;
  }>();
  const [activeStreamCamera, setActiveStreamCamera] = useState<{
    id: number;
    name: string;
  }>();
  const [streamModal, setStreamModal] = useState<{
    name: string;
    url: string;
  }>();

  const { data: operators = [] } = useCommandCentreOperators(siteId);
  const [eventsPageNum, setEventsPageNum] = useState(1);
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const { data: positionsData } = useCommandCentrePositions(siteId);
  const positions = positionsData ?? EMPTY_POSITIONS;
  const trackingThresholds = useCommandCentreTrackingThresholds();
  const decayedPositions = useOperatorDecayTimer(positions, trackingThresholds);
  const { data: eventsPage } = useCommandCentreEvents(
    siteId,
    liveExpanded ? eventsPageNum : 1,
    eventTypes,
  );
  const { data: cameras = [] } = useCommandCentreCameras(siteId);
  const {
    data: cameraStream,
    isFetching: isCameraStreamLoading,
    isError: isCameraStreamError,
    isFetched: isCameraStreamFetched,
  } = useCommandCentreCameraStream(activeStreamCamera?.id);
  const { data: operatorProfile } = useCommandCentreOperator(
    selectedUserId,
    siteId,
  );

  useCommandCentreSocket(siteId);

  useEffect(() => {
    if (!sites.length) return;
    if (siteId != null && sites.some((s) => s.locationId === siteId)) return;
    setSiteId(sites[0].locationId);
  }, [siteId, sites]);

  useEffect(() => {
    if (siteId) sessionStorage.setItem(PANEL_KEYS.siteId, String(siteId));
  }, [siteId]);

  useEffect(() => {
    setMapCenter(undefined);
    setEventsPageNum(1);
    setEventTypes([]);
    setSelectedUserId(undefined);
    setJobAssignmentOperator(undefined);
    setStreamModal(undefined);
    setActiveStreamCamera(undefined);
  }, [siteId]);

  useEffect(() => {
    if (!activeStreamCamera) return;

    if (cameraStream?.streamUrl) {
      setStreamModal({
        name: activeStreamCamera.name,
        url: cameraStream.streamUrl,
      });
      setActiveStreamCamera(undefined);
      return;
    }

    if (
      isCameraStreamError ||
      (isCameraStreamFetched && !isCameraStreamLoading)
    ) {
      setActiveStreamCamera(undefined);
    }
  }, [
    activeStreamCamera,
    cameraStream,
    isCameraStreamError,
    isCameraStreamFetched,
    isCameraStreamLoading,
  ]);

  const trackingByOperatorId = useMemo(
    () =>
      new Map<number, CommandCentrePosition>(
        decayedPositions.map((p) => [p.operatorId, p]),
      ),
    [decayedPositions],
  );

  const sortedOperators = useMemo(
    () =>
      mergeOperatorsWithSitePositions(
        operators,
        decayedPositions,
        (operatorId) => `${t('ccOperator')} ${operatorId}`,
      ),
    [decayedPositions, operators, t],
  );

  const operatorNameById = useMemo(
    () =>
      new Map<number, string>(
        sortedOperators.map((o) => [o.userId, o.displayName]),
      ),
    [sortedOperators],
  );

  const operatorMarkers = useMemo(
    () =>
      decayedPositions
        .filter((p) => p.lat != null && p.lng != null)
        .map(
          (p): OperatorMapMarker => ({
            lat: Number(p.lat),
            lng: Number(p.lng),
            operatorId: p.operatorId,
            presence: p.presence,
            linkHealth: p.linkHealth,
            label:
              operatorNameById.get(p.operatorId) ??
              `${t('ccOperator')} ${p.operatorId}`,
          }),
        ),
    [decayedPositions, operatorNameById, t],
  );

  const getOperatorPresence = useCallback(
    (userId: number): OperatorPresence | undefined =>
      trackingByOperatorId.get(userId)?.presence,
    [trackingByOperatorId],
  );

  const events: LiveUpdateRow[] = eventsPage?.items ?? [];

  const openOperator = useCallback(
    (userId: number) => setSelectedUserId(userId),
    [],
  );

  const openConversation = useCallback(
    (userId: number) => {
      const operator = sortedOperators.find(
        (candidate) => candidate.userId === userId,
      );
      setSelectedUserId(undefined);
      openGlobalConversation({
        userId,
        displayName: operator?.displayName,
      });
    },
    [openGlobalConversation, sortedOperators],
  );

  const openJobAssignment = useCallback(
    (operator: Pick<CommandCentreOperator, 'userId' | 'displayName'>) =>
      setJobAssignmentOperator({
        userId: operator.userId,
        displayName: operator.displayName,
      }),
    [],
  );

  const startOperatorCall = useCallback(
    async (userId: number) => {
      if (commsStatus !== 'ready' || callStatus !== 'idle') return;
      const roomId = directRooms.get(userId);
      if (!roomId) return;
      try {
        setSelectedUserId(undefined);
        await startCall(roomId, userId);
      } catch {
        return;
      }
    },
    [callStatus, commsStatus, directRooms, startCall],
  );

  const renderWorkrate = (v: number) => {
    const level = getWorkrateLevel(v);
    return (
      <span className="cc__workrate">
        <span style={{ color: WORKRATE_COLORS[level] }}>{v}%</span>
        <Icon
          icon={faWifi}
          color={WORKRATE_COLORS[level]}
          style={{ fontSize: 12 }}
        />
      </span>
    );
  };

  const renderNameCell = useCallback(
    (name: string, row: CommandCentreOperator) => (
      <>
        <button
          type="button"
          className="cc__link"
          onClick={(e) => {
            e.stopPropagation();
            openOperator(row.userId);
          }}
        >
          {name}
        </button>
      </>
    ),
    [openOperator],
  );

  const renderOperatorActions = useCallback(
    (userId: number, row: CommandCentreOperator) => (
      <span className="cc__operator-actions">
        <OperatorConversationButton
          displayName={row.displayName}
          unreadCount={unreadCounts.get(userId) ?? 0}
          disabled={commsStatus !== 'ready'}
          onOpen={() => openConversation(userId)}
        />
        {canAllocateJobs && (
          <Tooltip title={t('ccAssignJob')}>
            <Button
              type="text"
              className="cc__operator-action"
              aria-label={t('ccAssignJobToOperator', {
                operator: row.displayName,
              })}
              icon={<Icon icon={faBriefcase} />}
              onClick={(event) => {
                event.stopPropagation();
                openJobAssignment(row);
              }}
            />
          </Tooltip>
        )}
      </span>
    ),
    [
      canAllocateJobs,
      commsStatus,
      openConversation,
      openJobAssignment,
      t,
      unreadCounts,
    ],
  );

  const liveUpdatesColumns: CustomColumns<LiveUpdateRow> = useMemo(
    () => [
      {
        title: t('ccWho'),
        dataIndex: 'id',
        render: (_, row) => (
          <button
            type="button"
            className="cc__link"
            onClick={(e) => {
              e.stopPropagation();
              if (row.who.userId) openOperator(row.who.userId);
            }}
          >
            {row.who.displayName}
          </button>
        ),
      },
      { title: t('ccWhat'), dataIndex: 'what' },
      {
        title: t('ccUpdate'),
        dataIndex: 'update',
        ellipsis: true,
      },
      {
        title: t('ccWhen'),
        dataIndex: 'when',
        render: (v: string) => dayjs(v).format('h:mm A'),
      },
    ],
    [openOperator, t],
  );

  const userTableColumns: CustomColumns<CommandCentreOperator> = useMemo(
    () =>
      tableExpanded
        ? [
            {
              title: t('ccName'),
              dataIndex: 'displayName',
              render: renderNameCell,
            },
            {
              title: t('ccWorkrate'),
              dataIndex: 'workratePercent',
              render: renderWorkrate,
              width: 100,
              align: 'center',
            },
            {
              title: t('ccJobsCompleted'),
              dataIndex: 'jobsCompleted',
              width: 100,
              align: 'center',
            },
            {
              title: t('ccCurrentStatus'),
              dataIndex: 'userId',
              render: (userId: number, row: CommandCentreOperator) => {
                const presence =
                  getOperatorPresence(userId) ??
                  (row.currentStatus === 'Live' ? 'Online' : 'Offline');
                const linkHealth = trackingByOperatorId.get(userId)?.linkHealth;
                return (
                  <>
                    <span
                      className={`cc__status-dot cc__status-dot--${presenceCssClass(presence)}`}
                    />
                    {presenceLabel(presence)}
                    {linkHealth === 'Degrading' ? ` ${t('ccWeakLink')}` : ''}
                  </>
                );
              },
              width: 140,
              align: 'center',
            },
            {
              title: t('ccMetresCovered'),
              dataIndex: 'metresCovered',
              width: 120,
              align: 'center',
            },
            {
              title: t('ccWorkToRestRatio'),
              dataIndex: 'workToRestRatio',
              width: 120,
              align: 'center',
            },
            {
              title: t('actions'),
              dataIndex: 'userId',
              render: renderOperatorActions,
              width: 110,
              align: 'center',
            },
          ]
        : [
            {
              title: t('ccName'),
              dataIndex: 'displayName',
              render: renderNameCell,
              width: 120,
            },
            {
              title: t('ccWorkrate'),
              dataIndex: 'workratePercent',
              render: renderWorkrate,
              width: 100,
              align: 'center',
            },
            {
              title: t('actions'),
              dataIndex: 'userId',
              render: renderOperatorActions,
              width: 110,
              align: 'center',
            },
          ],
    [
      getOperatorPresence,
      renderNameCell,
      renderOperatorActions,
      t,
      tableExpanded,
      trackingByOperatorId,
    ],
  );

  return (
    <PageContainer>
      <div className="d-flex justify-content-between align-items-center">
        <Title
          title={t('commandCentre')}
          icon={faMapMarkerAlt}
          color={accent}
        />
        <div className="d-flex align-items-center gap-3">
          <CommandCentreCommsBootstrap />
          <span
            className="d-flex align-items-center gap-2"
            title={
              signalRStatus === 'connected'
                ? t('ccLiveConnected')
                : signalRStatus === 'connecting'
                  ? t('ccLiveConnecting')
                  : signalRStatus === 'reconnecting'
                    ? t('ccLiveReconnecting')
                    : signalRStatus === 'failed'
                      ? t('ccLiveFailed')
                      : t('ccLiveConnecting')
            }
          >
            <span
              className={`cc__status-dot cc__status-dot--${
                signalRStatus === 'connected'
                  ? 'online'
                  : signalRStatus === 'failed'
                    ? 'offline'
                    : 'away'
              }`}
            />
            <span className="small text-muted">
              {signalRStatus === 'connected'
                ? t('ccLiveConnected')
                : signalRStatus === 'failed'
                  ? t('ccLiveFailed')
                  : signalRStatus === 'reconnecting'
                    ? t('ccLiveReconnecting')
                    : t('ccLiveConnecting')}
            </span>
          </span>
          <Select
            style={{ minWidth: 200 }}
            placeholder={t('ccSelectSite')}
            value={siteId}
            onChange={setSiteId}
            options={sites.map((s) => ({
              value: s.locationId,
              label: s.locationName || s.locationCode,
            }))}
          />
        </div>
      </div>

      <div className="cc-workspace">
        <CompoundPreview
          map={compoundMap}
          siteId={siteId}
          onActiveChange={setCompoundPreviewActive}
        />
        <div className="cc">
          <div className="cc__map-area">
            <SiteMap
              operatorMarkers={operatorMarkers}
              onOperatorMarkerClick={openOperator}
              siteId={siteId}
              center={mapCenter}
              zoom={mapCenter ? 18 : undefined}
              kmlUrl="/kml/spots.kml"
              showMapSettings={false}
              showSiteOverlays
              satelliteView={satelliteView}
              onMapReady={setCompoundMap}
              suppressLegacyLayers={compoundPreviewActive}
            />

            {!tableExpanded ? (
              <>
                <div className="cc__map-controls">
                  <Switch
                    checked={satelliteView}
                    onChange={setSatelliteView}
                    checkedChildren={t('ccSatellite')}
                    unCheckedChildren={t('ccStreet')}
                  />
                </div>

                <div className="cc__module cc__module--live-updates">
                  <div className="cc__hide-row">
                    <span>{t('ccHideLiveUpdates')}</span>
                    <Switch
                      size="small"
                      checked={!hideLiveUpdates}
                      onChange={(v) => {
                        setHideLiveUpdates(!v);
                        localStorage.setItem(
                          PANEL_KEYS.liveUpdates,
                          String(!v),
                        );
                      }}
                    />
                  </div>
                  {!hideLiveUpdates && (
                    <div
                      className={`cc__panel${liveExpanded ? ' expanded' : ''}`}
                    >
                      <div className="cc__panel-body">
                        {liveExpanded ? (
                          <>
                            <Select
                              mode="multiple"
                              allowClear
                              placeholder={t('ccEventTypeFilter')}
                              style={{ width: '100%', marginBottom: 8 }}
                              options={eventTypeOptions}
                              value={eventTypes}
                              onChange={(v) => {
                                setEventTypes(v);
                                setEventsPageNum(1);
                              }}
                            />
                            <CustomTable
                              size="small"
                              dataSource={events}
                              rowKey="id"
                              columns={liveUpdatesColumns}
                              pagination={{
                                current: eventsPage?.page ?? eventsPageNum,
                                pageSize: eventsPage?.pageSize ?? 20,
                                total: eventsPage?.totalCount ?? 0,
                                showSizeChanger: false,
                              }}
                              onChange={(pagination) => {
                                if (pagination.current) {
                                  setEventsPageNum(pagination.current);
                                }
                              }}
                            />
                          </>
                        ) : events.length > 0 ? (
                          <ul className="cc__compact-list">
                            {events.slice(0, 3).map((row) => (
                              <li key={row.id}>
                                <button
                                  type="button"
                                  className="cc__link"
                                  onClick={() =>
                                    row.who.userId &&
                                    openOperator(row.who.userId)
                                  }
                                >
                                  {row.who.displayName}
                                </button>{' '}
                                {row.update}{' '}
                                <span className="cc__compact-time">
                                  ({dayjs(row.when).format('h:mm A')})
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="cc__empty-text">
                            {t('ccNoLiveUpdates')}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="cc__edge-btn cc__edge-btn--right"
                        aria-label={
                          liveExpanded ? t('ccCollapse') : t('ccExpand')
                        }
                        onClick={() => {
                          const next = !liveExpanded;
                          setLiveExpanded(next);
                          if (!next) setEventsPageNum(1);
                          sessionStorage.setItem(
                            PANEL_KEYS.liveExpanded,
                            String(next),
                          );
                        }}
                      >
                        <Icon
                          icon={liveExpanded ? faChevronLeft : faChevronRight}
                        />
                      </button>
                    </div>
                  )}
                </div>

                <div className="cc__module cc__module--cameras">
                  <div className="cc__hide-row">
                    <span>
                      {hideCameras ? t('ccShowCameras') : t('ccHideCameras')}
                    </span>
                    <Switch
                      size="small"
                      checked={!hideCameras}
                      onChange={(v) => {
                        setHideCameras(!v);
                        localStorage.setItem(PANEL_KEYS.cameras, String(!v));
                      }}
                    />
                  </div>
                  {!hideCameras && (
                    <div className="cc__panel">
                      {cameras.length > 0 ? (
                        <ul className="cc__camera-list">
                          {cameras.map((cam) => (
                            <li key={cam.id}>
                              <span>
                                <span
                                  className={`cc__status-dot cc__status-dot--${cam.isOnline ? 'online' : 'offline'}`}
                                  title={
                                    cam.isOnline
                                      ? t('ccOnline')
                                      : t('ccOffline')
                                  }
                                />
                                {cam.name}
                              </span>
                              <span className="cc__camera-actions">
                                <button
                                  type="button"
                                  className="cc__link"
                                  disabled={
                                    isCameraStreamLoading &&
                                    activeStreamCamera?.id === cam.id
                                  }
                                  onClick={() => {
                                    void queryClient.resetQueries({
                                      queryKey: [
                                        QUERY_KEYS.commandCentreCameraStream,
                                        cam.id,
                                      ],
                                    });
                                    setActiveStreamCamera({
                                      id: cam.id,
                                      name: cam.name,
                                    });
                                  }}
                                >
                                  {isCameraStreamLoading &&
                                  activeStreamCamera?.id === cam.id
                                    ? t('ccLoading')
                                    : t('ccGoLive')}
                                </button>
                                <button
                                  type="button"
                                  className="cc__link"
                                  disabled={
                                    cam.latitude == null ||
                                    cam.longitude == null
                                  }
                                  onClick={() => {
                                    if (
                                      cam.latitude == null ||
                                      cam.longitude == null
                                    ) {
                                      return;
                                    }
                                    setMapCenter({
                                      lat: cam.latitude,
                                      lng: cam.longitude,
                                    });
                                  }}
                                >
                                  {t('ccViewOnMap')}
                                </button>
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="cc__empty-text">{t('ccNoCameras')}</div>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : null}

            <div className="cc__module cc__module--users">
              <div className="cc__hide-row">
                <span>{t('ccHideTable')}</span>
                <Switch
                  size="small"
                  checked={!hideTable}
                  onChange={(v) => {
                    setHideTable(!v);
                    localStorage.setItem(PANEL_KEYS.table, String(!v));
                  }}
                />
              </div>
              {!hideTable && (
                <div
                  className={`cc__panel cc__panel--users${tableExpanded ? ' expanded' : ''}`}
                >
                  <button
                    type="button"
                    className="cc__edge-btn cc__edge-btn--left"
                    aria-label={tableExpanded ? t('ccCollapse') : t('ccExpand')}
                    onClick={() => {
                      const next = !tableExpanded;
                      setTableExpanded(next);
                      sessionStorage.setItem(
                        PANEL_KEYS.tableExpanded,
                        String(next),
                      );
                    }}
                  >
                    <Icon
                      icon={tableExpanded ? faChevronRight : faChevronLeft}
                    />
                  </button>
                  <div className="cc__panel-body">
                    <CustomTable
                      size="small"
                      pagination={false}
                      dataSource={sortedOperators}
                      rowKey="userId"
                      columns={userTableColumns}
                      scroll={{ x: 'max-content' }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={!!selectedUserId}
        onCancel={() => {
          setJobAssignmentOperator(undefined);
          setSelectedUserId(undefined);
        }}
        footer={null}
        title={operatorProfile?.displayName ?? t('ccOperator')}
      >
        {operatorProfile ? (
          <>
            <p>
              {t('ccName')}: <strong>{operatorProfile.displayName}</strong>
            </p>
            <p>
              {t('ccStatus')}:{' '}
              {(() => {
                const presence = selectedUserId
                  ? (getOperatorPresence(selectedUserId) ??
                    (operatorProfile.currentStatus === 'Live'
                      ? 'Online'
                      : 'Offline'))
                  : 'Offline';
                const linkHealth = selectedUserId
                  ? trackingByOperatorId.get(selectedUserId)?.linkHealth
                  : undefined;
                return (
                  <>
                    <span
                      className={`cc__status-dot cc__status-dot--${presenceCssClass(presence)}`}
                    />
                    <strong>{presenceLabel(presence)}</strong>
                    {linkHealth === 'Degrading' ? ` ${t('ccWeakLink')}` : ''}
                  </>
                );
              })()}
            </p>
            <OperatorProfileActions
              commsReady={commsStatus === 'ready'}
              callAvailable={commsStatus === 'ready' && callStatus === 'idle'}
              canAllocateJobs={canAllocateJobs}
              onOpenConversation={() =>
                selectedUserId && openConversation(selectedUserId)
              }
              onStartCall={() =>
                selectedUserId && void startOperatorCall(selectedUserId)
              }
              onAllocateJob={() => {
                if (selectedUserId && operatorProfile) {
                  openJobAssignment({
                    userId: selectedUserId,
                    displayName: operatorProfile.displayName,
                  });
                }
              }}
            />
          </>
        ) : (
          <div className="cc__operator-loading" role="status" aria-busy="true">
            <Spin size="small" />
            <span>{t('ccLoading')}</span>
          </div>
        )}
      </Modal>

      {jobAssignmentOperator && siteId && (
        <JobAssignmentModal
          open
          operatorId={jobAssignmentOperator.userId}
          operatorDisplayName={jobAssignmentOperator.displayName}
          siteId={siteId}
          onClose={() => setJobAssignmentOperator(undefined)}
          onAssigned={() => {
            void queryClient.invalidateQueries({
              queryKey: [
                QUERY_KEYS.commandCentreOperator,
                jobAssignmentOperator.userId,
              ],
            });
            void queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.commandCentreOperators, siteId],
            });
          }}
        />
      )}

      <Modal
        open={!!streamModal}
        onCancel={() => setStreamModal(undefined)}
        footer={null}
        title={streamModal?.name}
        width={720}
      >
        {streamModal?.url && (
          <iframe
            title={streamModal.name}
            src={streamModal.url}
            sandbox="allow-scripts allow-same-origin"
            style={{ width: '100%', height: 400, border: 0 }}
          />
        )}
      </Modal>
    </PageContainer>
  );
};
